/**
 * ResReleaseExplainer — progressive-disclosure explainer for RES.RELEASE (R2).
 *
 * RES.RELEASE is the compact-closed side of the RES.BALANCE dual pair. It
 * neutralizes the vulnerability class "resource acquired in scope is never
 * released" by typing acquisition as a coevaluation η and discharge as the
 * evaluation counit ε: the snake equations force every acquired dual to be
 * discharged, so the leaking computation is structurally inexpressible
 * (CONTAINED mode).
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { RES_RELEASE_META } from "@csk/core";
import { THEOREM_COLOR, withAlpha } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";
import {
  ExplainerCard,
  StringDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import { TheoremMetaStrip } from "./TheoremMetaStrip.js";
import { TIER_ONE_IMMUNITY_LINK } from "./tierOneCrossLinks.js";

const ACCENT = THEOREM_COLOR["RES.RELEASE"];

const IDENTITY: ExplainerIdentity = {
  id: "explainer-res-release",
  kind: "SSM · Neutralization Theorem (S1)",
  title: "RES.RELEASE",
  subtitle: "R2 · Compact closed · CONTAINED · G4 Quantity",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Compact-closed (structure)", href: "#explainer-compact-closed", accent: ACCENT },
  {
    label: "RES.CONSUME (dual)",
    href: "#explainer-res-consume",
    accent: THEOREM_COLOR["RES.CONSUME"],
  },
  TIER_ONE_IMMUNITY_LINK,
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>RES.RELEASE</strong> says: anything acquired in a scope is released when the scope
      exits, by construction. A computation that leaks a handle is not one you can write.
    </p>
  ),
  intuition: (
    <>
      <p>
        Leaks are the complement of exhaustion: not "spent too much" but "never gave it back" — the
        unclosed file, the dangling lock, the container handle that escapes. The usual safeguard is
        a <code>finally</code> clause or a destructor, a story that depends on every path
        remembering to clean up.
      </p>
      <p>
        CSK types acquisition as a coevaluation{" "}
        <MathInline>{tex`\eta : I \to A^{*} \otimes A`}</MathInline> and release as the evaluation
        counit <MathInline>{tex`\varepsilon : A \otimes A^{*} \to I`}</MathInline> in a
        compact-closed category. The snake equations make an acquired dual <em>have</em> to be
        discharged; the leaking computation is unconstructable rather than swept up by cleanup code.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        In a compact-closed category every object <MathInline>{tex`A`}</MathInline> has a dual{" "}
        <MathInline>{tex`A^{*}`}</MathInline> with unit{" "}
        <MathInline>{tex`\eta_{A} : I \to A^{*} \otimes A`}</MathInline> (acquire) and counit{" "}
        <MathInline>{tex`\varepsilon_{A} : A \otimes A^{*} \to I`}</MathInline> (release),
        satisfying the snake equations{" "}
        <MathInline>{tex`(\varepsilon \otimes 1)(1 \otimes \eta) = 1`}</MathInline> and{" "}
        <MathInline>{tex`(1 \otimes \varepsilon)(\eta \otimes 1) = 1`}</MathInline>.
      </p>
      <p style={{ marginTop: "0.5rem" }}>
        A scope that acquires without discharging would leave a <em>cup</em> with no matching{" "}
        <em>cap</em> — a non-identity wire that the snake equation forbids. The leaking term is
        therefore not closed. <code>RES.RELEASE</code> is a projection of the Categorical Immunity
        Theorem and pairs with <code>RES.CONSUME</code> as <code>RES.BALANCE</code>.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Vulnerability class neutralized:</strong> missing release of a resource after its
        lifetime — memory, file descriptors, locks (CWE-401, CWE-772, CWE-775; e.g., CVE-2019-5736).
        Realized by <em>structural inexpressibility</em> of the unmatched acquisition; the mode is{" "}
        <strong>CONTAINED</strong> — execution may run, but the open obligation cannot escape its
        scope.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> If you acquire a handle through a raw host call
        outside the compact-closed scope, you have left the kernel. RES.RELEASE is then vacuous on
        that handle — the discharge obligation has gone away, not been bypassed.
      </p>
      <p>
        <strong>Seam type</strong> (per <code>theory/structural/SEAM-THEORY.md</code>):{" "}
        {RES_RELEASE_META.seamType}. <strong>Bridge</strong>: compact counit (linear scope) — the
        governance artifact is the explicit ε discharge.
      </p>
    </>
  ),
  references: [
    {
      label: "RES.RELEASE theorem card",
      path: "docs/reference/theorem-cards/RES-RELEASE.md",
    },
    {
      label: "Kernel module (main theorem RELEASE-neutralized)",
      path: `agda/${RES_RELEASE_META.agdaSource}`,
      note: `RES_RELEASE_META.agdaSource is "${RES_RELEASE_META.agdaSource}"; the repo path is prefixed with "agda/". RELEASE-neutralized at line 113 (module lines 91–235).`,
    },
    {
      label: "Theorem Canon (§4.3 CONTAINED; §6 RES.BALANCE; §7 ACCOUNT SDP)",
      path: "docs/reference/Theorem-Canon.md",
    },
  ],
};

const DIAGRAM = (
  <>
    <TheoremMetaStrip
      theoremName="RES.RELEASE"
      code="R2"
      structure="Compact closed"
      mode="contained"
      generator="G4"
      seamType={RES_RELEASE_META.seamType}
      bridgeArtifact="compact counit (linear scope)"
      theoremColor={ACCENT}
      washColor={withAlpha(ACCENT, "1A")}
    />
    <div style={{ marginTop: "0.5rem" }}>
      <StringDiagram
        accent={ACCENT}
        ariaLabel="Compact-closed string diagram: a cup (acquire η) must be matched by a cap (release ε); the snake equation straightens the wire"
      />
    </div>
  </>
);

export function ResReleaseExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={DIAGRAM} crossLinks={CROSS_LINKS} />
  );
}
